import { useState, useEffect, useCallback } from 'react'
import { getReactions, toggleReaction, updateReactionNote } from '../services/reactionService'

/**
 * Hook for managing Legacy Reactions on a piece of content.
 *
 * @param {string} contentType - 'story' | 'memory' | 'milestone' | 'time_capsule'
 * @param {string|number} contentId - The content's ID
 * @returns {Object} Reaction state and actions
 */
export function useReactions(contentType, contentId) {
  const [reactions, setReactions] = useState([])
  const [myReactions, setMyReactions] = useState([])
  const [grouped, setGrouped] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [togglingType, setTogglingType] = useState(null)

  const fetchReactions = useCallback(async () => {
    if (!contentType || !contentId) return

    setLoading(true)
    setError(null)
    try {
      const data = await getReactions(contentType, contentId)
      setReactions(data.reactions || [])
      setMyReactions(data.my_reactions || [])
      setGrouped(data.grouped || {})
    } catch (err) {
      console.error('Error fetching reactions:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [contentType, contentId])

  useEffect(() => {
    fetchReactions()
  }, [fetchReactions])

  const handleToggle = useCallback(
    async (reactionType, note = '') => {
      setTogglingType(reactionType)
      try {
        const result = await toggleReaction(reactionType, contentType, contentId, note)
        setMyReactions(result.my_reactions || [])
        // Re-fetch to get updated grouped data with reactor details
        await fetchReactions()
        return result
      } catch (err) {
        console.error('Error toggling reaction:', err)
        setError(err)
        return null
      } finally {
        setTogglingType(null)
      }
    },
    [contentType, contentId, fetchReactions]
  )

  const handleUpdateNote = useCallback(
    async (reactionId, note) => {
      try {
        await updateReactionNote(reactionId, note)
        await fetchReactions()
      } catch (err) {
        console.error('Error updating note:', err)
        setError(err)
      }
    },
    [fetchReactions]
  )

  const hasReaction = useCallback(
    (reactionType) => {
      return myReactions.includes(reactionType)
    },
    [myReactions]
  )

  return {
    reactions,
    myReactions,
    grouped,
    loading,
    error,
    togglingType,

    toggle: handleToggle,
    updateNote: handleUpdateNote,
    refetch: fetchReactions,

    hasReaction,
  }
}

export default useReactions
